// src/features/chat/hooks/useChatFeed.js

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { fetchOlderMessages, subscribeToMessages } from '../services/chatRepo';
import { normalizeMessage, sortMessagesAsc } from '../models/chatModel';
import { getTargetKey, isGroupTarget } from '../models/chatTarget';

const PAGE_SIZE_DEFAULT = 25;

/**
 * Initial state for the chat feed reducer.
 */
const initialState = {
  byId: new Map(),      // id -> normalized message object
  orderedIds: [],       // sorted ascending (oldest first)
  cursor: null,         // pagination cursor (oldest loaded doc snapshot)
  hasMore: false,       // start false; first snapshot determines actual value
  status: 'idle',       // 'idle' | 'loading' | 'ready' | 'error'
  error: null,
  loadingMore: false,
  initialLoaded: false, // true after first snapshot processed
  version: 0,           // bump for memoization stability
};

/**
 * Check if two messages are meaningfully different.
 * Used to avoid unnecessary state updates.
 */
function messagesAreDifferent(prev, next) {
  if (!prev) return true;
  // Compare key fields that might change
  return (
    prev.tsMs !== next.tsMs ||
    prev.text !== next.text ||
    prev.inReplyTo !== next.inReplyTo ||
    prev.uiType !== next.uiType ||
    prev.recordCitations?.length !== next.recordCitations?.length
  );
}

/**
 * Reducer for chat feed state management.
 * Key principle: MERGE by ID, never replace and lose older pages.
 */
function reducer(state, action) {
  switch (action.type) {
    case 'RESET':
      return { ...initialState, status: action.idle ? 'idle' : 'loading' };

    case 'LIVE_SNAPSHOT': {
      const { docs, initialCursorCandidate, initialHasMoreCandidate, target } = action;

      // Copy-on-write Map
      const byId = new Map(state.byId);
      let changed = false;

      for (const rawDoc of docs) {
        const msg = normalizeMessage(rawDoc, { target });
        const prev = byId.get(msg.id);

        // Only update if different
        if (messagesAreDifferent(prev, msg)) {
          byId.set(msg.id, msg);
          changed = true;
        }
      }

      // Early return if nothing changed and we're already ready
      if (!changed && state.status === 'ready') {
        return state;
      }

      // Re-sort by timestamp (ascending - oldest first)
      const ordered = Array.from(byId.values())
        .sort(sortMessagesAsc)
        .map((m) => m.id);

      return {
        ...state,
        byId,
        orderedIds: ordered,
        status: 'ready',
        error: null,
        initialLoaded: true,
        // Only set cursor/hasMore on first load (don't let new messages reset pagination)
        cursor: state.cursor ?? initialCursorCandidate ?? null,
        hasMore: state.cursor !== null ? state.hasMore : !!initialHasMoreCandidate,
        version: state.version + 1,
      };
    }

    case 'LOAD_MORE_START':
      return { ...state, loadingMore: true };

    case 'LOAD_MORE_SUCCESS': {
      const { docs, nextCursor, hasMore, target } = action;

      const byId = new Map(state.byId);
      let changed = false;

      for (const rawDoc of docs) {
        const msg = normalizeMessage(rawDoc, { target });
        // Only add if not already present (don't overwrite newer versions)
        if (!byId.has(msg.id)) {
          byId.set(msg.id, msg);
          changed = true;
        }
      }

      const ordered = changed
        ? Array.from(byId.values())
            .sort(sortMessagesAsc)
            .map((m) => m.id)
        : state.orderedIds;

      return {
        ...state,
        byId,
        orderedIds: ordered,
        cursor: nextCursor ?? state.cursor,
        hasMore,
        loadingMore: false,
        version: state.version + (changed ? 1 : 0),
      };
    }

    case 'LOAD_MORE_ERROR':
      return { ...state, loadingMore: false, error: action.error };

    case 'LIVE_ERROR':
      return { ...state, status: 'error', error: action.error };

    default:
      return state;
  }
}

/**
 * Hook for managing chat messages with real-time updates and pagination.
 *
 * Key features:
 * - Merge-by-id strategy: pagination + realtime never lose data
 * - Stable message references for memoization
 * - Proper cursor management for pagination
 *
 * @param {import('../models/chatTarget').ChatTarget} target - Chat target (private or group)
 * @param {Object} [options]
 * @param {number} [options.pageSize=25] - Number of messages per page
 * @param {boolean} [options.enabled=true] - Whether to subscribe to messages
 * @returns {Object} Feed state and controls
 */
export function useChatFeed(target, { pageSize = PAGE_SIZE_DEFAULT, enabled = true } = {}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  // Create stable dependency key for target
  const targetKey = getTargetKey(target);
  const joinedAtMillis = isGroupTarget(target) && target.joinedAt
    ? target.joinedAt.toMillis?.() || 0
    : 0;
  const canSubscribe = !isGroupTarget(target) || !!target.joinedAt;

  // Subscribe to real-time updates
  useEffect(() => {
    // When disabled, stay idle - don't start subscription
    if (!enabled) {
      dispatch({ type: 'RESET', idle: true });
      return undefined;
    }

    dispatch({ type: 'RESET', idle: false });

    // For group chats, wait until we have joinedAt to satisfy Firestore rules
    if (!canSubscribe) {
      return undefined;
    }

    let unsub = null;
    try {
      unsub = subscribeToMessages(
        target,
        pageSize,
        ({ docs, cursorDoc, hasMore }) => {
          dispatch({
            type: 'LIVE_SNAPSHOT',
            docs,
            initialCursorCandidate: cursorDoc,
            initialHasMoreCandidate: hasMore,
            target,
          });
        },
        (error) => {
          console.error('Chat feed subscription error:', error);
          dispatch({ type: 'LIVE_ERROR', error });
        }
      );
    } catch (error) {
      console.error('Chat feed subscription error:', error);
      dispatch({ type: 'LIVE_ERROR', error });
      return undefined;
    }

    return () => unsub?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, joinedAtMillis, pageSize, canSubscribe, enabled]); // target is intentionally not included (derived from targetKey)

  // Memoized messages array - only changes when orderedIds or byId changes
  const messages = useMemo(() => {
    return state.orderedIds
      .map((id) => state.byId.get(id))
      .filter(Boolean);
  }, [state.orderedIds, state.byId]);

  // Load more (pagination)
  const loadMore = useCallback(async () => {
    if (!canSubscribe || !state.cursor || state.loadingMore || !state.hasMore) {
      return;
    }

    dispatch({ type: 'LOAD_MORE_START' });

    try {
      const res = await fetchOlderMessages(target, {
        cursorDoc: state.cursor,
        pageSize: pageSizeRef.current,
      });

      dispatch({
        type: 'LOAD_MORE_SUCCESS',
        docs: res.docs,
        nextCursor: res.nextCursor,
        hasMore: res.hasMore,
        target,
      });
    } catch (error) {
      console.error('Error loading more messages:', error);
      dispatch({ type: 'LOAD_MORE_ERROR', error });
    }
  }, [canSubscribe, state.cursor, state.loadingMore, state.hasMore, target]);

  return {
    messages,
    status: state.status,
    error: state.error,
    hasMore: state.hasMore,
    loadingMore: state.loadingMore,
    loadMore,
    initialLoaded: state.initialLoaded,
    // Expose byId map for advanced use cases (e.g., reply lookup)
    getMessageById: useCallback((id) => state.byId.get(id), [state.byId]),
  };
}
